import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { SITE_URL } from "@/constants/site";

interface ArticleLayoutProps {
  title: string;
  description: string;
  date: string;
  readTime: string;
  imageUrl: string;
  keywords: string;
  articleSchemaName: string;
  children: React.ReactNode;
  faqItems?: { question: string; answer: string }[];
}

export default function ArticleLayout({
  title,
  description,
  date,
  readTime,
  imageUrl,
  keywords,
  articleSchemaName,
  children,
  faqItems,
}: ArticleLayoutProps) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: description,
    datePublished: date,
    dateModified: "2026-06-25",
    author: {
      "@type": "Organization",
      name: articleSchemaName,
    },
    image: imageUrl,
    inLanguage: "pt",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Início", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: title },
    ],
  };

  const faqPageSchema = faqItems && faqItems.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      }
    : null;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      {faqPageSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
        />
      )}
      <title>{title} | CSSBuy Spreadsheet</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            title={title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6">
            <h1 className="text-white text-2xl md:text-4xl lg:text-5xl font-extrabold text-center leading-tight max-w-3xl drop-shadow-md">
              {title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-white/75 text-xs md:text-sm">
              <span>{date}</span>
              <span>·</span>
              <span>{readTime} de leitura</span>
              <span>·</span>
              <span>CSSBuy Spreadsheet</span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="w-full bg-background-50 py-12 md:py-20">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
              {children}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-primary-500 text-white font-semibold px-8 py-3 rounded-full text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-200 inline-flex items-center gap-2"
                >
                  Aceder à Planilha CSSBuy
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="/"
                  className="text-foreground-600 font-medium text-sm hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  ← Voltar ao Início
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}